import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Palmtree, CalendarIcon, Clock, Check, X, Trash2 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

const VACATION_STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  denied: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export const VacationTab = ({
  vacationRequests,
  canManageProjects,
  user,
  onRequestVacation,
  onReviewRequest,
  onDeleteRequest
}) => {
  return (
    <div className="space-y-6">
      {/* Request Vacation Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {canManageProjects ? 'All Vacation Requests' : 'My Vacation Requests'}
        </h2>
        <Button onClick={onRequestVacation} data-testid="request-vacation-btn"
          className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
          <Palmtree className="w-4 h-4 mr-2" />Request Vacation
        </Button>
      </div>

      {/* Vacation Requests List */}
      <div className="space-y-3">
        {vacationRequests.length === 0 ? (
          <Card className="bg-[#2A3240] border-white/10">
            <CardContent className="py-12 text-center">
              <Palmtree className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">No vacation requests yet</p>
            </CardContent>
          </Card>
        ) : (
          vacationRequests.map((request) => {
            const days = differenceInDays(parseISO(request.end_date), parseISO(request.start_date)) + 1;
            return (
              <Card key={request.id} className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white">{request.employee_name}</h3>
                        <Badge className={VACATION_STATUS_COLORS[request.status]}>
                          {request.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {request.status === 'approved' && <Check className="w-3 h-3 mr-1" />}
                          {request.status === 'denied' && <X className="w-3 h-3 mr-1" />}
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {format(parseISO(request.start_date), 'dd MMM')} - {format(parseISO(request.end_date), 'dd MMM yyyy')}
                        </span>
                        <span className="text-[#F5A623]">{days} day{days !== 1 ? 's' : ''}</span>
                      </div>
                      {request.reason && <p className="text-sm text-gray-500 mt-2">{request.reason}</p>}
                      {request.admin_notes && (
                        <div className="mt-2 p-2 bg-[#1E2530] rounded text-xs text-gray-400">
                          <span className="font-semibold">Admin Note:</span> {request.admin_notes}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canManageProjects && (
                        <Button variant="outline" size="sm" onClick={() => onReviewRequest(request)}
                          className="border-white/20 text-gray-300 hover:bg-white/5">
                          Review
                        </Button>
                      )}
                      {(request.status === 'pending' || user?.role === 'admin') && request.employee_id === user?.id && (
                        <Button variant="ghost" size="sm" onClick={() => onDeleteRequest(request.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
